import React, { useState } from 'react';
import { Box, Typography, TextField, Button, Alert, Paper } from '@mui/material';
import SEOHead from '../components/SEO/SEOHead';
import ContactService from '../services/contact-service';

const ContactPage: React.FC = () => {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = 'Name is required.';
    if (!form.email.trim()) e.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Invalid email address.';
    if (!form.subject.trim()) e.subject = 'Subject is required.';
    if (!form.message.trim()) e.message = 'Message is required.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!validate()) return;
    setSubmitting(true);
    try {
      await ContactService.submitContactForm(form);
      setSuccess(true);
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch {
      setSubmitError('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [field]: e.target.value });
    if (errors[field]) setErrors({ ...errors, [field]: '' });
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto' }}>
      <SEOHead
        title="Contact Us"
        description="Get in touch with the Hooliah team. We'd love to hear from you."
        canonicalUrl="https://hooliah.com/contact"
      />

      <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1 }}>Contact Us</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Have a question or feedback? Fill out the form below or email us directly at support@hooliah.com.
      </Typography>

      {success && <Alert severity="success" sx={{ mb: 2 }}>Your message has been sent. We'll get back to you soon!</Alert>}
      {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label="Name" value={form.name} onChange={handleChange('name')} error={!!errors.name} helperText={errors.name} required />
          <TextField label="Email" type="email" value={form.email} onChange={handleChange('email')} error={!!errors.email} helperText={errors.email} required />
          <TextField label="Subject" value={form.subject} onChange={handleChange('subject')} error={!!errors.subject} helperText={errors.subject} required />
          <TextField label="Message" multiline minRows={4} value={form.message} onChange={handleChange('message')} error={!!errors.message} helperText={errors.message} required />
          <Button type="submit" variant="contained" size="large" disabled={submitting}>
            {submitting ? 'Sending…' : 'Send Message'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default ContactPage;
